import { useQuery } from '@apollo/client';
import _ from 'lodash';
import moment from 'moment';
import { useObserver } from 'mobx-react';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useCart from 'hooks/use-cart';
import { useCatalogRequestContext } from 'src/contexts/catalog-request-context';
import { FilteredSpecialsDocument } from 'src/gql/graphql';
import { parseDates, specialIsActive, specialMatchesMenuType } from 'shared/helpers/specials';
import { determinePlatformType, mapOrderTypeToPreOrderType } from 'shared/helpers/enum-mappings';
import { useStores } from 'contexts/stores';
import { useGetDegradedPollInterval } from 'shared/hooks/use-get-degraded-poll-interval';
import { SPECIALS_POLL_INTERVAL_MS, SPECIALS_POLL_DEGRADATION_MULTIPLIER } from './specials-polling.constants';

export default function useDispensarySpecials(props) {
  const dispensaryHookData = useDispensary();
  // These are used to account for the various places where the hook could be called from
  const { UI } = useStores();
  const uiDispensary = useObserver(() => UI?.dispensary);
  const isKiosk = useObserver(() => UI?.isKiosk ?? false);

  const cart = useCart();
  const cartDispensary = useObserver(() => cart.dispensary);

  const dispensary = dispensaryHookData?.dispensary || uiDispensary || cartDispensary;
  const { menuType, orderType } = useCatalogRequestContext();
  const enableLLxSaleDiscountSync = dispensary?.storeSettings?.enableLLxSaleDiscountSync;

  const { specialId, showPaymentRestricted, skip: skipQuery = false } = props || {};

  const basePollInterval = useGetDegradedPollInterval({
    milliseconds: SPECIALS_POLL_INTERVAL_MS,
    degradationMultiplier: SPECIALS_POLL_DEGRADATION_MULTIPLIER,
  });
  const pollInterval = isKiosk ? basePollInterval : 0;

  const variables = {
    specialsFilter: {
      dispensaryId: dispensary?.id,
      ...(specialId ? { specialIds: [specialId], current: true, past: true } : { current: true }),
      platformType: determinePlatformType(UI),
      preOrderType: mapOrderTypeToPreOrderType(orderType),
    },
  };

  const { data, ...apolloValues } = useQuery(FilteredSpecialsDocument, {
    skip: !dispensary || skipQuery,
    variables,
    fetchPolicy: pollInterval > 0 ? 'cache-and-network' : undefined,
    pollInterval,
  });

  const filteredSpecialsData = data?.filteredSpecials?.specials || [];
  const parsedFilteredSpecials = _.map(filteredSpecialsData, parseDates);
  const [currentSpecials] = _.reduce(
    parsedFilteredSpecials,
    (accumulator, special) => {
      const llxEnabled =
        special.source === 'POS' &&
        ((special.specialType === 'sale' && enableLLxSaleDiscountSync) || special.specialType === 'bogo');
      const now = moment();
      const isPending = special.startStamp && now < special.startStamp;
      const isActive = specialIsActive(special, dispensary?.timezone);
      const acceptableSpecial = !isPending && isActive;

      if (special.source !== 'POS' || llxEnabled) {
        accumulator[
          // passing in specialId indicates we should show whatever special data exists, even if the special is not active
          (acceptableSpecial || specialId) &&
          specialMatchesMenuType(special, menuType) &&
          (!special.paymentRestrictions?.payByBankSignupIncentive || showPaymentRestricted)
            ? 0
            : 1
        ].push(special);
      }
      return accumulator;
    },
    [[], []]
  );

  const specials = [..._.sortBy(currentSpecials, ({ startStamp }) => startStamp)];
  const hasSpecials = specials?.length > 0;

  return { specials, hasSpecials, ...apolloValues };
}
